import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiChevronDown } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Breadcrumbs from './Breadcrumbs';
import { pageBreadcrumbs } from '../data/breadcrumbs';
import { pageFaqs } from '../data/faqs';
import { notFoundMeta, pageMeta } from '../data/pageMeta';
import { setBreadcrumbJsonLd } from '../lib/breadcrumbSchema';
import { setFaqJsonLd } from '../lib/faqSchema';
import { setPageMeta } from '../lib/setPageMeta';
import '../styles/home.css';

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMobileDropdown, setActiveMobileDropdown] = useState(null);

  useEffect(() => {
    setPageMeta(pageMeta[location.pathname] || notFoundMeta, location.pathname);
    setFaqJsonLd(pageFaqs[location.pathname]);
    setBreadcrumbJsonLd(pageBreadcrumbs[location.pathname]);
  }, [location.pathname]);

  const dropdownItems = [
    {
      label: 'Services',
      href: '/services',
      dropdown: [
        'All Services',
        'Managed Websites',
        'Web Design and Development',
        'Business Email',
        'Technical Support',
        'Managed Hosting',
        'Technology Consulting'
      ]
    },
    {
      label: 'Agency',
      href: '/agency-services',
      dropdown: [
        'Agency Overview',
        'White-Label WordPress Management',
        'White-Label Technical Support',
        'Agency Hosting',
        'Agency Plans'
      ]
    },
    {
      label: 'Company',
      href: '/about',
      dropdown: ['Who We Help', 'About', 'Get Started', 'Support', 'Contact']
    }
  ];

  const getLink = (label) => {
    const l = label.toLowerCase();

    if (l === 'all services') return '/services';
    if (l === 'agency overview') return '/agency-services';
    if (l === 'who we help') return '/who-we-help';
    if (l === 'about') return '/about';
    if (l === 'support') return '/support';
    if (l === 'contact') return '/contact';
    if (l === 'get started') return '/get-started';

    if (l.includes('agency plans')) return '/agency-plans';
    if (l.includes('white-label wordpress management')) return '/agency-services/white-label-wordpress';
    if (l.includes('white-label technical support')) return '/agency-services/white-label-support';
    if (l.includes('agency hosting')) return '/agency-services/agency-hosting';
    if (l.includes('managed websites')) return '/services/website-management';
    if (l.includes('technical support')) return '/services/technical-support';
    if (l.includes('business email')) return '/services/business-email';
    if (l.includes('managed hosting')) return '/services/managed-hosting';
    if (l.includes('web design and development') || l.includes('web design')) return '/services/web-design-and-development';
    if (l.includes('technology consulting') || l.includes('fractional cto')) return '/services/technology-consulting';

    return '/services';
  };

  const toggleMobileDropdown = (label) => {
    setActiveMobileDropdown(activeMobileDropdown === label ? null : label);
  };

  return (
    <div className="home-page min-h-screen flex flex-col text-[#17211E] bg-[#FAF8F3]">
      <header className="sticky top-0 z-50 bg-white border-b border-[#17211E]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 gap-6">
            <Link to="/" className="flex-shrink-0">
              <img
                src="/managedsite-logo.png?v=3"
                alt="ManagedSite.net"
                className="h-12 w-auto lg:h-14"
              />
            </Link>

            <nav className="hidden lg:flex items-center gap-7">
              {dropdownItems.map((item) => (
                <div key={item.label} className="relative group">
                  <Link
                    to={item.href}
                    className="flex items-center py-8 text-[15px] font-semibold text-[#17211E] transition-colors hover:text-[#174C3C]"
                  >
                    {item.label}
                    <SafeIcon icon={FiChevronDown} className="ml-1 w-4 h-4" />
                  </Link>
                  <div className="invisible absolute left-0 top-full z-10 w-64 border border-[#17211E]/10 bg-white opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                    <div className="py-2">
                      {item.dropdown.map((subItem) => (
                        <Link
                          key={subItem}
                          to={getLink(subItem)}
                          className="block px-5 py-2.5 text-sm font-medium text-[#17211E] hover:bg-[#EAF1EC] hover:text-[#174C3C]"
                        >
                          {subItem}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </nav>

            <div className="hidden lg:flex items-center">
              <Link to="/digital-review" className="home-button px-7 py-3.5">
                Request a Digital Review
              </Link>
            </div>

            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-[#17211E] p-2"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <SafeIcon icon={mobileMenuOpen ? FiX : FiMenu} className="w-8 h-8" />
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-4 pt-2 pb-8 space-y-1 max-h-screen overflow-y-auto">
            {dropdownItems.map((item) => (
              <div key={item.label} className="border-b border-gray-50 last:border-0">
                <button
                  onClick={() => toggleMobileDropdown(item.label)}
                  className="flex justify-between items-center w-full px-3 py-4 text-lg font-bold text-[#17211E]"
                >
                  {item.label}
                  <SafeIcon icon={FiChevronDown} className={`w-5 h-5 transition-transform ${activeMobileDropdown === item.label ? 'rotate-180' : ''}`} />
                </button>
                {activeMobileDropdown === item.label && (
                  <div className="bg-[#FAF8F3] px-4 py-2 space-y-1">
                    {item.dropdown.map((subItem) => (
                      <Link
                        key={subItem}
                        to={getLink(subItem)}
                        className="block px-3 py-3 text-base text-[#3B7C6D] font-semibold"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {subItem}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="mt-4 px-3">
              <Link to="/digital-review" className="home-button w-full">
                Request a Digital Review
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-grow">
        <Breadcrumbs crumbs={pageBreadcrumbs[location.pathname]} />
        {children}
      </main>

      <footer className="bg-[#17211E] text-[#FAF8F3] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 flex flex-col gap-10 md:grid md:grid-cols-2 lg:flex lg:flex-row lg:items-start lg:justify-between lg:gap-12">
            <div className="max-w-[21rem] shrink-0">
              <Link to="/" className="mb-6 inline-block">
                <img
                  src="/managedsite-logo-white.png?v=10"
                  alt="ManagedSite.net"
                  className="h-12 w-auto lg:h-14"
                />
              </Link>
              <p className="text-[#FAF8F3]/80 leading-relaxed mb-5 text-base">
                Websites, business email, hosting, security, and technical support managed together through one dependable technical partner.
              </p>
              <p className="text-base text-[#FAF8F3]/80 leading-relaxed">
                New work starts on the contact form. Clients get a phone number and support desk after onboarding.
              </p>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg mb-6">Services</h3>
              <ul className="space-y-3.5 text-lg text-[#FAF8F3]/80">
                <li><Link to="/services/website-management" className="hover:text-[#D7B77A] transition-colors">Managed Websites</Link></li>
                <li><Link to="/services/web-design-and-development" className="hover:text-[#D7B77A] transition-colors">Web Design</Link></li>
                <li><Link to="/services/business-email" className="hover:text-[#D7B77A] transition-colors">Business Email</Link></li>
                <li><Link to="/services/technical-support" className="hover:text-[#D7B77A] transition-colors">Technical Support</Link></li>
                <li><Link to="/services/managed-hosting" className="hover:text-[#D7B77A] transition-colors">Managed Hosting</Link></li>
                <li><Link to="/services/technology-consulting" className="hover:text-[#D7B77A] transition-colors">Technology Consulting</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg mb-6">Agency Services</h3>
              <ul className="space-y-3.5 text-lg text-[#FAF8F3]/80">
                <li><Link to="/agency-services" className="hover:text-[#D7B77A] transition-colors text-[#D7B77A] font-bold">Agency Overview</Link></li>
                <li><Link to="/agency-services/white-label-wordpress" className="hover:text-[#D7B77A] transition-colors">White-Label WordPress Management</Link></li>
                <li><Link to="/agency-services/white-label-support" className="hover:text-[#D7B77A] transition-colors">White-Label Technical Support</Link></li>
                <li><Link to="/agency-services/agency-hosting" className="hover:text-[#D7B77A] transition-colors">Agency Hosting</Link></li>
                <li><Link to="/agency-plans" className="hover:text-[#D7B77A] transition-colors">Agency Plans</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold text-lg mb-6">Company</h3>
              <ul className="space-y-3.5 text-lg text-[#FAF8F3]/80">
                <li><Link to="/who-we-help" className="hover:text-[#D7B77A] transition-colors">Who We Help</Link></li>
                <li><Link to="/about" className="hover:text-[#D7B77A] transition-colors">About</Link></li>
                <li><Link to="/get-started" className="hover:text-[#D7B77A] transition-colors">Get Started</Link></li>
                <li><Link to="/contact" className="hover:text-[#D7B77A] transition-colors">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-[#D7B77A] transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-[#D7B77A] transition-colors">Terms of Service</Link></li>
                <li><Link to="/support" className="hover:text-[#D7B77A] transition-colors">Support</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-10 border-t border-white/10 text-sm text-[#FAF8F3]/50">
            <p>© 2026 MyGoto Brands, Inc. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;